<template>
    <Dialog v-model:visible="visible" header="Create New Assessment" :modal="true" :style="{ width: '450px' }">
        <div class="space-y-4">
            <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <InputText v-model="form.name" class="w-full" placeholder="e.g., Mid-Term Test" />
            </div>

            <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Max Score</label>
                <InputNumber v-model="form.max_score" class="w-full" :min="1" :max="100" />
            </div>

            <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <Select v-model="form.type" :options="typeOptions" optionLabel="label" optionValue="value"
                    class="w-full" />
            </div>

            <div class="mb-6">
                <div class="flex items-center justify-between">
                    <label class="text-sm font-medium text-gray-700">Make this reusable?</label>
                    <InputSwitch v-model="form.isReusable" />
                </div>
                <p v-if="form.isReusable" class="text-xs text-primary mt-1">
                    Recommended. This assessment can be used in your report card templates forever.
                </p>
                <p v-else class="text-xs text-orange-600 mt-1">
                    This assessment will ONLY be available for this specific term.
                </p>
            </div>

            <div v-if="error" class="mt-2 text-red-500 text-sm">{{ error.message }}</div>
        </div>

        <template #footer>
            <div class="flex justify-end space-x-3">
                <button @click="$emit('close')" class="px-4 py-2 text-gray-600 hover:text-gray-800">Cancel</button>
                <button @click="createAssessment" :disabled="loading"
                    class="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50">
                    {{ loading ? 'Creating...' : 'Create Assessment' }}
                </button>
            </div>
        </template>
    </Dialog>
</template>

<script setup lang="ts">
import { useCreateAssessmentTypeMutation } from '@/types/vue-apollo-helpers';

const props = defineProps<{
    isOpen: boolean;
    academicCalendarId?: string; // Current term ID
}>();

const emit = defineEmits(['close', 'created']);

const visible = computed({
  get: () => props.isOpen,
  set: (value) => {
    if (!value) emit('close');
  },
});

const form = reactive({
  name: '',
  max_score: 100,
  type: 'CA',
  isReusable: true,
});

const typeOptions = [
  { label: 'Continuous Assessment (CA)', value: 'CA' },
  { label: 'Examination', value: 'EXAM' },
];

const { mutate: createAssessmentMutation, loading, error } = useCreateAssessmentTypeMutation();

async function createAssessment() {
  if (!form.name) return;

  try {
    const input = {
      name: form.name,
      max_score: form.max_score,
      is_ca: form.type === 'CA',
      is_exam: form.type === 'EXAM',
      // If reusable, send null for academic_calendar_id. If not, send the current term ID.
      academic_calendar_id: form.isReusable ? null : props.academicCalendarId,
      // Default values
      sequence: 0,
      // We might need subject_id or classroom_id depending on backend requirements,
      // but for "Generic" assessments they might be optional or global.
      // Assuming global for now based on requirements.
    };

    const result = await createAssessmentMutation({ input });

    if (result?.data?.createAssessmentType) {
      emit('created', result.data.createAssessmentType);
      emit('close');
      // Reset form
      form.name = '';
      form.max_score = 100;
      form.type = 'CA';
      form.isReusable = true;
    }
  } catch {
    // Ignore: on failure the modal stays open for retry.
  }
}
</script>
